/**
 * How many of the tier's pixels the frame is actually rendered over.
 *
 * A multiplier on `tierSettings().renderScale` rather than an absolute fraction, so the two knobs
 * stay one decision each: the tier says what this machine can afford, and this says how far the
 * player disagrees. At 1 the tier's own answer stands; below it the frame trades pixels for rate on
 * any tier alike; above it a machine with headroom supersamples past what its tier chose.
 *
 * It is its own knob because it is the one fill-rate lever that moves every post effect at once,
 * which `qualityTier.ts` already calls out, and the one a player reaches for first when the frame
 * rate dips.
 *
 * Announced rather than polled: the scale is applied to the surface when it is set, so the pipeline
 * subscribes rather than asking per frame.
 */

import { clamp } from '@/sim/math';

export const DEFAULT_RENDER_SCALE_MULTIPLIER = 1;

/** A quarter of the pixels. Below this the frame is legible only as a mood. */
export const MIN_RENDER_SCALE_MULTIPLIER = 0.5;

/** Supersampling past this costs quadratically and reads identically. */
export const MAX_RENDER_SCALE_MULTIPLIER = 1.5;

let multiplier = DEFAULT_RENDER_SCALE_MULTIPLIER;
const listeners = new Set<() => void>();

export function renderScaleMultiplier(): number {
  return multiplier;
}

/** Clamped here as well as in the settings layer: this is the value the surface actually takes. */
export function setRenderScaleMultiplier(value: number): void {
  const next = clamp(value, MIN_RENDER_SCALE_MULTIPLIER, MAX_RENDER_SCALE_MULTIPLIER);
  if (next === multiplier) return;
  multiplier = next;
  for (const listener of listeners) listener();
}

/** Subscribes to the knob moving. Returns an unsubscribe, which a pipeline calls on dispose. */
export function onRenderScaleChange(listener: () => void): () => void {
  listeners.add(listener);
  return (): void => {
    listeners.delete(listener);
  };
}
